import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';

// Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Parts from './pages/Parts';
import Services from './pages/Services';
import Products from './pages/Products';
import Quotes from './pages/Quotes';
import QuoteDetail from './pages/QuoteDetail';
import QuoteNew from './pages/QuoteNew';
import MaterialTakeoffs from './pages/MaterialTakeoffs';
import MaterialTakeoffDetail from './pages/MaterialTakeoffDetail';
import WorkOrders from './pages/WorkOrders';
import OrdersCalendar from './pages/OrdersCalendar';
import Schedule from './pages/Schedule';
import OrderDetail from './pages/OrderDetail';
import TimeTracking from './pages/TimeTracking';
import TimeTrackingKiosk from './pages/TimeTrackingKiosk';
import TimeEntries from './pages/TimeEntries';
import Timecards from './pages/Timecards';
import Admin from './pages/Admin';
import ProcurementPO from './pages/ProcurementPO';
import ProcurementPODetail from './pages/ProcurementPODetail';
import ProcurementVendors from './pages/ProcurementVendors';
import ProcurementVendorDetail from './pages/ProcurementVendorDetail';
import ProcurementReceiving from './pages/ProcurementReceiving';
import ProcurementReceivingDetail from './pages/ProcurementReceivingDetail';
import ProcurementInvoices from './pages/ProcurementInvoices';
import HumanResources from './pages/HumanResources';
import ClockedInToday from './pages/ClockedInToday';
import ExpenseReimbursements from './pages/ExpenseReimbursements';
import ExpenseReimbursementDetail from './pages/ExpenseReimbursementDetail';
import Inventory from './pages/Inventory';
import Consumables from './pages/Consumables';
import RawMaterials from './pages/RawMaterials';
import Quality from './pages/Quality';
import PackingSlips from './pages/PackingSlips';
import PackingSlipDetail from './pages/PackingSlipDetail';
import CustomerInvoices from './pages/CustomerInvoices';
import CustomerInvoiceDetail from './pages/CustomerInvoiceDetail';
import CustomerPayments from './pages/CustomerPayments';
import PaymentDetail from './pages/PaymentDetail';
import ARAging from './pages/ARAging';
import SalesReport from './pages/SalesReport';
import PayrollReport from './pages/PayrollReport';
import TrainingDocuments from './pages/TrainingDocuments';
import TrainingSignoffs from './pages/TrainingSignoffs';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import KioskActivateAccount from './pages/KioskActivateAccount';
import EmployeePortalLayout from './layouts/EmployeePortalLayout';
import SalesPortalLayout from './layouts/SalesPortalLayout';
import EmployeeTraining from './pages/employee/EmployeeTraining';
import EmployeeTrainingDetail from './pages/employee/EmployeeTrainingDetail';
import EmployeeTrainingSignoffs from './pages/employee/EmployeeTrainingSignoffs';
import EmployeeReceipts from './pages/employee/EmployeeReceipts';
import EmployeeTimeOff from './pages/employee/EmployeeTimeOff';
import EmployeeMessages from './pages/employee/EmployeeMessages';
import EmployeePaystubs from './pages/employee/EmployeePaystubs';
import EmployeeProfile from './pages/employee/EmployeeProfile';
import SalesLeads from './pages/sales/SalesLeads';
import SalesLeadDetail from './pages/sales/SalesLeadDetail';
import SalesActivityLog from './pages/sales/SalesActivityLog';
import SalesLogActivity from './pages/sales/SalesLogActivity';
import SalesCustomers from './pages/sales/SalesCustomers';
import SalesCustomerDetail from './pages/sales/SalesCustomerDetail';
import SalesQuoteRequests from './pages/sales/SalesQuoteRequests';
import SalesQuoteRequestForm from './pages/sales/SalesQuoteRequestForm';
import SalesExpenses from './pages/sales/SalesExpenses';
import SalesExpenseNew from './pages/sales/SalesExpenseNew';
import SalesExpenseDetail from './pages/sales/SalesExpenseDetail';
import SalesProfile from './pages/sales/SalesProfile';
import SalesGuide from './pages/sales/SalesGuide';
import Leads from './pages/Leads';
import QuoteRequests from './pages/QuoteRequests';
import TransportationJobs from './pages/TransportationJobs';
import TransportationJobDetail from './pages/TransportationJobDetail';
import TransportationPortal from './pages/transport/TransportationPortal';
import ShopTasks from './pages/ShopTasks';
import ShopTaskDetail from './pages/ShopTaskDetail';
import LaborAllocation from './pages/LaborAllocation';
import LaborByOrderReport from './pages/LaborByOrderReport';
import TaskAdoptionReport from './pages/TaskAdoptionReport';
import TimeOffRequests from './pages/TimeOffRequests';
import EmployeeMessagesAdmin from './pages/EmployeeMessagesAdmin';

// Context
import { AuthProvider, useAuth } from './hooks/useAuth';
import ImpersonationBanner from './components/ImpersonationBanner';
import { DocumentTabsProvider, useDocumentTabs } from './context/DocumentTabsContext';
import DocumentTabBar from './components/DocumentTabBar';
import { isKioskPilotUser } from './constants/kiosk';
import ThemeModeProvider from './theme/ThemeModeProvider';
import CompanyProvider from './context/CompanyProvider';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</Box>;
  }

  if (!isAuthenticated) {
    const redirect = `${location.pathname}${location.search || ''}`;
    const params = new URLSearchParams();
    if (redirect && redirect !== '/' && !redirect.startsWith('/login')) {
      params.set('redirect', redirect);
    }
    const qs = params.toString();
    return <Navigate to={qs ? `/login?${qs}` : '/login'} replace />;
  }

  // Basic employees: read-only profile + logout only.
  // Shop kiosk pilot uses role "employee" but must reach / and /time-tracking/kiosk (not profile-only).
  if (user?.role === 'employee') {
    if (isKioskPilotUser(user)) {
      const kioskAllowed = new Set(['/', '/profile', '/logout', '/time-tracking/kiosk']);
      if (!kioskAllowed.has(location.pathname)) {
        return <Navigate to="/time-tracking/kiosk" replace />;
      }
    } else {
      const path = location.pathname;
      const allowed =
        path === '/logout' ||
        path.startsWith('/employee/');
      if (!allowed) {
        return <Navigate to="/employee/training" replace />;
      }
    }
  }

  // Outside Sales Rep: Sales Portal only
  if (user?.role === 'sales_rep') {
    const path = location.pathname;
    const allowed = path === '/logout' || path.startsWith('/sales/');
    if (!allowed) {
      return <Navigate to="/sales/leads" replace />;
    }
  }

  // Estimator: quotes → orders → packing slips (+ customers for quoting)
  if (user?.role === 'estimator') {
    const path = location.pathname;
    const allowed =
      path === '/logout' ||
      path === '/profile' ||
      path === '/' ||
      path.startsWith('/quotes') ||
      path.startsWith('/work-orders') ||
      path.startsWith('/packing-slips') ||
      path.startsWith('/customers') ||
      path.startsWith('/parts') ||
      path.startsWith('/services') ||
      path.startsWith('/products');
    if (!allowed) {
      return <Navigate to="/quotes" replace />;
    }
  }

  // Sales Portal is sales_rep-only (admins, managers, employees cannot open /sales/*)
  if (location.pathname.startsWith('/sales/') && user?.role !== 'sales_rep') {
    return <Navigate to="/" replace />;
  }

  return children;
};

/** Remount on each history entry so /quotes/new is never a stale form (same path revisited). */
const QuoteNewWithLocationKey = () => {
  const { key } = useLocation();
  return <QuoteNew key={key} />;
};

/** Remount when :id changes (e.g. P.O. 5 → new) so in-app navigation does not keep prior PO state. */
const ProcurementPODetailByIdKey = () => {
  const { id } = useParams();
  return <ProcurementPODetail key={id} />;
};

// Root route: if user is kiosk pilot, always send them to kiosk (saved session → straight to kiosk)
const KioskRedirectOrDashboard = () => {
  const { user } = useAuth();
  if (isKioskPilotUser(user)) {
    return <Navigate to="/time-tracking/kiosk" replace />;
  }
  if (user?.role === 'employee') {
    return <Navigate to="/employee/training" replace />;
  }
  if (user?.role === 'sales_rep') {
    return <Navigate to="/sales/leads" replace />;
  }
  if (user?.role === 'estimator') {
    return <Navigate to="/quotes" replace />;
  }
  return (
    <MainLayout>
      <Dashboard />
    </MainLayout>
  );
};

// Visiting /logout clears session and redirects to login (no button on kiosk)
const LogoutRedirect = () => {
  const { logout } = useAuth();
  React.useEffect(() => {
    logout();
  }, [logout]);
  return <Navigate to="/login" replace />;
};

const LegacyReceivingDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/procurement/shipments-in/${id}` : '/procurement/shipments-in'} replace />;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const location = useLocation();
  const { tabs } = useDocumentTabs();
  const { user } = useAuth();
  const isBasicEmployee = user?.role === 'employee';

  const breadcrumbItems = React.useMemo(() => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return [];

    const isIdSegment = (value) => /^\d+$/.test(value);
    const titleize = (value) => value.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const push = (label, to) => items.push({ label, to });
    const items = [];

    if (segments[0] === 'material-takeoffs') {
      push('Sales', null);
      push('Material Takeoffs', '/material-takeoffs');
      if (segments[1] && isIdSegment(segments[1])) push('Takeoff Detail', null);
      return items;
    }
    if (segments[0] === 'quotes') {
      push('Sales', null);
      push('Quotes', '/quotes');
      if (segments[1] === 'new') push('New Quote', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Quote Detail', null);
      return items;
    }
    if (segments[0] === 'work-orders') {
      push('Sales', null);
      push('Orders', '/work-orders');
      if (segments[1] === 'calendar') push('Calendar', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Order Detail', null);
      return items;
    }
    if (segments[0] === 'customers') {
      push('Sales', null);
      push('Customers', '/customers');
      if (segments[1] && isIdSegment(segments[1])) push('Customer Detail', null);
      return items;
    }
    if (segments[0] === 'customer-invoices') {
      push('Sales', null);
      push('Customer Invoices', '/customer-invoices');
      if (segments[1] && isIdSegment(segments[1])) push('Customer Invoice Detail', null);
      return items;
    }
    if (segments[0] === 'customer-payments') {
      push('Sales', null);
      push('Customer Payments', '/customer-payments');
      if (segments[1] && isIdSegment(segments[1])) push('Payment Detail', null);
      return items;
    }
    if (segments[0] === 'packing-slips') {
      push('Sales', null);
      push('Shipments', '/packing-slips');
      if (segments[1] === 'new') push('New Shipment', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Shipment Detail', null);
      return items;
    }
    if (segments[0] === 'transportation') {
      push('Transportation', '/transportation');
      if (segments[1] === 'new') push('New Job', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Job Detail', null);
      return items;
    }
    if (segments[0] === 'shop-tasks') {
      push('Shop Tasks', '/shop-tasks');
      if (segments[1] === 'new') push('New Task', null);
      else if (segments[1] === 'allocation') push('Labor Allocation', null);
      else if (segments[1] === 'labor-by-order') push('Labor by Work Order', null);
      else if (segments[1] === 'adoption') push('Task Adoption', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Task Detail', null);
      return items;
    }
    if (segments[0] === 'procurement') {
      push('Procurement', null);
      if (segments[1] === 'po') {
        push('Purchase Orders', '/procurement/po');
        if (segments[2] === 'new') push('New Purchase Order', null);
        else if (segments[2] && isIdSegment(segments[2])) push('Purchase Order Detail', null);
      } else if (segments[1] === 'shipments-in') {
        push('Shipments Received', '/procurement/shipments-in');
        if (segments[2] === 'new') push('New Shipment Received', null);
        else if (segments[2] && isIdSegment(segments[2])) push('Shipment Received Detail', null);
      } else if (segments[1] === 'vendors') {
        push('Vendors', '/procurement/vendors');
        if (segments[2] === 'new') push('New Vendor', null);
        else if (segments[2] && isIdSegment(segments[2])) push('Vendor Detail', null);
      } else if (segments[1] === 'invoices') {
        push('Vendor Invoices', '/procurement/invoices');
      } else {
        push(titleize(segments[1] || ''), null);
      }
      return items;
    }

    push(titleize(segments[0]), segments.length > 1 ? `/${segments[0]}` : null);
    for (let i = 1; i < segments.length; i += 1) {
      const seg = segments[i];
      const isLast = i === segments.length - 1;
      let label = isIdSegment(seg) ? 'Detail' : titleize(seg);
      if (seg === 'new') label = `New ${titleize(segments[i - 1] || 'Item')}`;
      push(label, isLast ? null : `/${segments.slice(0, i + 1).join('/')}`);
    }
    return items;
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} disableMenuButton={isBasicEmployee} />
      {!isBasicEmployee && <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          p: 1.5, // Reduced from p: 3 (24px to 12px)
          marginTop: '48px', // Reduced from 64px to match toolbar height
          // No margin-left needed - sidebar overlays content
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'background.default',
        }}
      >
        {tabs.length > 0 ? (
          <DocumentTabBar />
        ) : (
          breadcrumbItems.length > 0 && (
            <Breadcrumbs separator=">" sx={{ color: 'text.secondary', mb: 1 }}>
              {breadcrumbItems.map((item, idx) => {
                const isLast = idx === breadcrumbItems.length - 1;
                if (item.to && !isLast) {
                  return (
                    <Link
                      key={`${item.label}-${idx}`}
                      component={RouterLink}
                      to={item.to}
                      underline="hover"
                      color="inherit"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <Typography key={`${item.label}-${idx}`} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {item.label}
                  </Typography>
                );
              })}
            </Breadcrumbs>
          )
        )}
        {children}
      </Box>
    </Box>
  );
};

function App() {
  return (
    <CompanyProvider>
    <ThemeModeProvider>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ImpersonationBanner />
          <DocumentTabsProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/kiosk/activate-account" element={<KioskActivateAccount />} />
            <Route path="/transport/:token" element={<TransportationPortal />} />
            <Route path="/logout" element={<LogoutRedirect />} />
            <Route path="/kiosk" element={<Navigate to="/time-tracking/kiosk" replace />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <KioskRedirectOrDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Schedule />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Navigate to="/employee/profile" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/training"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeeTraining />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/training/signoffs"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeeTrainingSignoffs />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/training/:id"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeeTrainingDetail />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/receipts"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeeReceipts />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/time-off"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeeTimeOff />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/messages"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeeMessages />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/profile"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeeProfile />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/paystubs"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EmployeePaystubs />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/leads"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesLeads />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/leads/:id"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesLeadDetail />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/activity"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesActivityLog />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/activity/new"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesLogActivity />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/customers"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesCustomers />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/customers/:id"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesCustomerDetail />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/quote-requests"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesQuoteRequests />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/quote-requests/:id"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesQuoteRequestForm />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/expenses"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesExpenses />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/expenses/new"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesExpenseNew />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/expenses/:id"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesExpenseDetail />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/profile"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesProfile />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/guide"
              element={
                <ProtectedRoute>
                  <SalesPortalLayout>
                    <SalesGuide />
                  </SalesPortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Leads />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote-requests"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <QuoteRequests />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote-requests/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <QuoteRequests />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Customers />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parts"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Parts />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/services"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Services />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Products />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/material-takeoffs"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <MaterialTakeoffs />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/material-takeoffs/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <MaterialTakeoffDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Quotes />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <QuoteNewWithLocationKey />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <QuoteDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <WorkOrders />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders/calendar"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OrdersCalendar />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OrderDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-invoices"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerInvoices />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-invoices/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerInvoiceDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-payments"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerPayments />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-payments/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PaymentDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/ar-aging"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ARAging />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/sales-summary"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SalesReport />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/payroll-hours"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PayrollReport />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-tracking"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TimeTracking />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/clocked-in"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ClockedInToday />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/time-entries"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TimeEntries />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/timecards"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Timecards />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/expense-reimbursements"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ExpenseReimbursements />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/expense-reimbursements/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ExpenseReimbursementDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/time-off-requests"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TimeOffRequests />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employee-messages"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <EmployeeMessagesAdmin />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/my-training"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <EmployeeTraining />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/training-documents"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TrainingDocuments />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/training-signoffs"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TrainingSignoffs />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-tracking/kiosk"
              element={<TimeTrackingKiosk />}
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Admin />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/vendors/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementVendorDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/vendors"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementVendors />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/po"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementPO />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/po/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementPODetailByIdKey />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/receiving"
              element={<Navigate to="/procurement/shipments-in" replace />}
            />
            <Route
              path="/procurement/receiving/new"
              element={<Navigate to="/procurement/shipments-in/new" replace />}
            />
            <Route
              path="/procurement/receiving/:id"
              element={<LegacyReceivingDetailRedirect />}
            />
            <Route
              path="/procurement/shipments-in"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementReceiving />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/shipments-in/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementReceivingDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/shipments-in/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementReceivingDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/invoices"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementInvoices />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <HumanResources />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Inventory />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/consumables"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Consumables />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/raw-materials"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <RawMaterials />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packing-slips"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PackingSlips />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packing-slips/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PackingSlipDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packing-slips/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PackingSlipDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transportation"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TransportationJobs />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transportation/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TransportationJobDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transportation/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TransportationJobDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-tasks"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ShopTasks />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-tasks/allocation"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <LaborAllocation />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-tasks/labor-by-order"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <LaborByOrderReport />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-tasks/adoption"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TaskAdoptionReport />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-tasks/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ShopTaskDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quality"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Quality />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </DocumentTabsProvider>
        </Router>
      </AuthProvider>
    </ThemeModeProvider>
    </CompanyProvider>
  );
}

export default App;
